import type { Route } from "../../../../../utils/models/Route";
import type { PlanTrailState } from "../../modules/PlanTrailModule";
import type { UndoCommand, CommandContext } from "../types";

/**
 * Command for a change to the days themselves — adding or removing a day, or
 * spacing the days evenly. Any of these can rewrite an arbitrary number of
 * routes at once, so — like MergeAllRoutesCommand — this snapshots the whole
 * routes map wholesale, along with everything keyed by day, rather than trying
 * to invert the change algebraically.
 */
export class ApplyDayCountCommand implements UndoCommand {
  readonly type = 'APPLY_DAY_COUNT';
  readonly routeKey: string | null = null;

  private routesBefore: { [key: string]: Route };
  private routesAfter: { [key: string]: Route };
  private activeRouteKeyBefore: string | null;
  private activeRouteKeyAfter: string | null;
  private dayInfoBefore: PlanTrailState['dayInfo'];
  private dayInfoAfter: PlanTrailState['dayInfo'];
  private stageDestinationsBefore: PlanTrailState['stageDestinations'] | null;
  private stageDestinationsAfter: PlanTrailState['stageDestinations'] | null;
  /**
   * What the change did to the highlights — added, removed, or changed (as
   * they were, and as it left them). Only these come and go on undo and redo,
   * so highlights added since (which nothing else undoes) are left alone.
   */
  private highlightChanges: {
    added: PlanTrailState['hikeHighlights'];
    removed: PlanTrailState['hikeHighlights'];
    changedBefore: PlanTrailState['hikeHighlights'];
    changedAfter: PlanTrailState['hikeHighlights'];
  } | null;

  constructor(data: {
    routesBefore: { [key: string]: Route };
    routesAfter: { [key: string]: Route };
    activeRouteKeyBefore: string | null;
    activeRouteKeyAfter: string | null;
    dayInfoBefore: PlanTrailState['dayInfo'];
    dayInfoAfter: PlanTrailState['dayInfo'];
    stageDestinationsBefore?: PlanTrailState['stageDestinations'];
    stageDestinationsAfter?: PlanTrailState['stageDestinations'];
    /** Highlights before and after, for a change that adds some (the AI Planner's places to stay). */
    hikeHighlightsBefore?: PlanTrailState['hikeHighlights'];
    hikeHighlightsAfter?: PlanTrailState['hikeHighlights'];
  }) {
    const copy = <T>(value: T): T => JSON.parse(JSON.stringify(value));
    this.routesBefore = copy(data.routesBefore);
    this.routesAfter = copy(data.routesAfter);
    this.activeRouteKeyBefore = data.activeRouteKeyBefore;
    this.activeRouteKeyAfter = data.activeRouteKeyAfter;
    this.dayInfoBefore = copy(data.dayInfoBefore);
    this.dayInfoAfter = copy(data.dayInfoAfter);
    this.stageDestinationsBefore = data.stageDestinationsBefore ? copy(data.stageDestinationsBefore) : null;
    this.stageDestinationsAfter = data.stageDestinationsAfter ? copy(data.stageDestinationsAfter) : null;
    this.highlightChanges = null;
    if (data.hikeHighlightsBefore && data.hikeHighlightsAfter) {
      const before = new Map(data.hikeHighlightsBefore.map(highlight => [highlight.clientId, highlight]));
      const after = new Map(data.hikeHighlightsAfter.map(highlight => [highlight.clientId, highlight]));
      const added = data.hikeHighlightsAfter.filter(highlight => !before.has(highlight.clientId));
      const removed = data.hikeHighlightsBefore.filter(highlight => !after.has(highlight.clientId));
      const changed = data.hikeHighlightsAfter.filter(highlight =>
        before.has(highlight.clientId) && JSON.stringify(before.get(highlight.clientId)) !== JSON.stringify(highlight));
      if (added.length || removed.length || changed.length) {
        this.highlightChanges = copy({
          added,
          removed,
          changedBefore: changed.map(highlight => before.get(highlight.clientId)!),
          changedAfter: changed,
        });
      }
    }
  }

  /** Whether undoing or redoing this changes the highlights too (so they need redrawing). */
  get changesHighlights(): boolean {
    return this.highlightChanges !== null;
  }

  undo(ctx: CommandContext): void {
    this.apply(ctx, this.routesBefore, this.activeRouteKeyBefore, this.dayInfoBefore, this.stageDestinationsBefore);
    if (this.highlightChanges) {
      const { added, removed, changedBefore } = this.highlightChanges;
      this.swapHighlights(ctx, added, removed, changedBefore);
    }
  }

  redo(ctx: CommandContext): void {
    this.apply(ctx, this.routesAfter, this.activeRouteKeyAfter, this.dayInfoAfter, this.stageDestinationsAfter);
    if (this.highlightChanges) {
      const { added, removed, changedAfter } = this.highlightChanges;
      this.swapHighlights(ctx, removed, added, changedAfter);
    }
  }

  /**
   * Takes `drop` out of the highlights, puts `restore` back (those not there
   * already), and sets `revert` back to how they're given.
   */
  private swapHighlights(
    ctx: CommandContext,
    drop: PlanTrailState['hikeHighlights'],
    restore: PlanTrailState['hikeHighlights'],
    revert: PlanTrailState['hikeHighlights']
  ): void {
    const dropped = new Set(drop.map(highlight => highlight.clientId));
    const reverted = new Map(revert.map(highlight => [highlight.clientId, highlight]));
    const kept = ctx.state.hikeHighlights
      .filter(highlight => !dropped.has(highlight.clientId))
      .map(highlight => (reverted.has(highlight.clientId) ? JSON.parse(JSON.stringify(reverted.get(highlight.clientId))) : highlight));
    const present = new Set(kept.map(highlight => highlight.clientId));
    const missing = restore.filter(highlight => !present.has(highlight.clientId));
    ctx.state.hikeHighlights = [...kept, ...JSON.parse(JSON.stringify(missing))];
  }

  private apply(
    ctx: CommandContext,
    routes: { [key: string]: Route },
    activeRouteKey: string | null,
    dayInfo: PlanTrailState['dayInfo'],
    stageDestinations: PlanTrailState['stageDestinations'] | null
  ): void {
    ctx.state.routes = JSON.parse(JSON.stringify(routes));
    ctx.state.activeRouteKey = activeRouteKey;
    ctx.state.dayInfo = JSON.parse(JSON.stringify(dayInfo));
    // Older entries never captured destinations — leave them as they are.
    if (stageDestinations) {
      (ctx.state as any).stageDestinations = JSON.parse(JSON.stringify(stageDestinations));
    }
    ctx.state._statsVersion++;
  }
}
